import { z } from "zod";
import { dateStringSchema, transactionSchema, txStatusSchema, txExecutionResultSchema } from "./common.js";
import { TX_FILTER_DIMENSIONS, rangeParamNames } from "./tx-filters.js";
export const noteHashSchema = z.object({
    id: z.number(),
    txId: z.number(),
    value: z.string(),
    position: z.number(),
});
export const nullifierSchema = z.object({
    id: z.number(),
    txId: z.number(),
    value: z.string(),
    position: z.number(),
});
export const resolvedContractSchema = z.object({
    address: z.string(),
    label: z.string().nullable(),
    contractType: z.string().nullable(),
    storageSlotIndex: z.union([z.number(), z.string()]),
});
export const publicDataWriteSchema = z.object({
    id: z.number(),
    txId: z.number(),
    leafSlot: z.string(),
    value: z.string(),
    position: z.number(),
    resolvedContract: resolvedContractSchema.nullable(),
});
export const publicCallSchema = z.object({
    contractAddress: z.string(),
    functionSelector: z.string().nullable(),
    phase: z.enum(["setup", "app", "teardown"]),
    msgSender: z.string().nullable(),
    isStaticCall: z.boolean(),
    calldataSize: z.number(),
    calldata: z.array(z.string()),
    label: z.string().nullable(),
    contractType: z.string().nullable(),
});
export const l2ToL1MessageSchema = z.object({
    position: z.number(),
    messageHash: z.string().nullable(),
    recipient: z.string().nullable(),
    sender: z.string().nullable(),
    content: z.string().nullable(),
    fromPublicExecution: z.boolean(),
});
export const privateLogDetailSchema = z.object({
    index: z.number(),
    emittedLength: z.number(),
    fields: z.array(z.string()),
});
export const publicLogDetailSchema = z.object({
    index: z.number(),
    contractAddress: z.string().nullable(),
    emittedLength: z.number(),
    fields: z.array(z.string()),
});
export const contractClassLogDetailSchema = z.object({
    index: z.number(),
    contractAddress: z.string().nullable(),
    contractClassId: z.string().nullable(),
    emittedLength: z.number(),
    fields: z.array(z.string()),
});
export const addressAppearanceSchema = z.object({
    role: z.enum(["callee", "msgSender", "calldata", "l2ToL1Sender", "feePayer"]),
    phase: z.string().nullable(), // "setup" | "app" | "teardown" | null
    callIndex: z.number().nullable(), // 0-based, null for l2ToL1/feePayer
    calldataIndex: z.number().nullable(), // 0-based, null unless role=calldata
    functionSelector: z.string().nullable(), // null unless role=callee
});
export const publicAddressSchema = z.object({
    address: z.string(),
    label: z.string().nullable(),
    contractType: z.string().nullable(),
    appearances: z.array(addressAppearanceSchema),
});
export const privacySetSchema = z.object({
    clusterId: z.number(),
    clusterSize: z.number(),
    totalTxsAnalyzed: z.number(),
    outlierScore: z.number().nullable(),
});
export const feePricingDataSchema = z.object({
    costUsd: z.number(),
    costEth: z.number(),
    costFpa: z.number(),
    ethUsdPrice: z.number(),
    ethPerFeeAssetE12: z.string(),
    snapshotAgeSeconds: z.number().optional(),
});
export const similarTxSchema = z.object({
    txHash: z.string(),
    blockNumber: z.number().nullable(),
    status: z.string(),
    numNoteHashes: z.number(),
    numNullifiers: z.number(),
    numL2ToL1Msgs: z.number(),
    numPrivateLogs: z.number(),
    numContractClassLogs: z.number(),
    numPublicLogs: z.number().nullable(),
    gasLimitDa: z.number().nullable(),
    gasLimitL2: z.number().nullable(),
    maxFeePerDaGas: z.number().nullable(),
    maxFeePerL2Gas: z.number().nullable(),
    numSetupCalls: z.number(),
    numAppCalls: z.number(),
    hasTeardown: z.boolean(),
    totalPublicCalldataSize: z.number(),
    expirationTimestamp: z.number().nullable(),
    feePayer: z.string(),
    outlierScore: z.number().nullable(),
    featureVector: z.array(z.union([z.number(), z.string()])).nullable(),
});
export const txDetailResponseSchema = z.object({
    tx: transactionSchema,
    featureVector: z.array(z.union([z.number(), z.string()])).nullable(),
    noteHashes: z.array(noteHashSchema),
    nullifiers: z.array(nullifierSchema),
    publicDataWrites: z.array(publicDataWriteSchema),
    publicCalls: z.array(publicCallSchema),
    clusterMemberships: z.array(z.object({
        id: z.number(),
        runId: z.number(),
        txId: z.number(),
        clusterId: z.number(),
        membershipScore: z.number().nullable(),
        outlierScore: z.number().nullable(),
    })),
    privacySet: privacySetSchema.nullable(),
    similarTxs: z.array(similarTxSchema),
    l2ToL1Messages: z.array(l2ToL1MessageSchema),
    privateLogDetails: z.array(privateLogDetailSchema),
    publicLogDetails: z.array(publicLogDetailSchema),
    contractClassLogDetails: z.array(contractClassLogDetailSchema),
    publicAddresses: z.array(publicAddressSchema),
    feePayerPct: z.number(),
    feePricingData: feePricingDataSchema.nullable(),
});
/**
 * Row shape of the tx list endpoint — a hand-picked projection, deliberately
 * lighter than transactionSchema (no error/logs sizes/jsonb blobs).
 */
export const txListRowSchema = z.object({
    id: z.number(),
    networkId: z.string(),
    txHash: z.string(),
    status: txStatusSchema,
    executionResult: txExecutionResultSchema.nullable(),
    blockNumber: z.number().nullable(),
    txIndex: z.number().nullable(),
    actualFee: z.string().nullable(),
    numNoteHashes: z.number(),
    numNullifiers: z.number(),
    numL2ToL1Msgs: z.number(),
    numPrivateLogs: z.number(),
    numContractClassLogs: z.number(),
    numPublicDataWrites: z.number().nullable(),
    numPublicLogs: z.number().nullable(),
    numSetupCalls: z.number(),
    numAppCalls: z.number(),
    hasTeardown: z.boolean(),
    totalPublicCalldataSize: z.number(),
    gasLimitDa: z.number().nullable(),
    gasLimitL2: z.number().nullable(),
    maxFeePerDaGas: z.number().nullable(),
    maxFeePerL2Gas: z.number().nullable(),
    expirationTimestamp: z.number().nullable(),
    feePayer: z.string(),
    createdAt: dateStringSchema,
});
export const txListResponseSchema = z.object({
    data: z.array(txListRowSchema),
    page: z.number(),
    limit: z.number(),
    total: z.number(),
});
/** min/max range param per filter dimension, e.g. minNumNullifiers / maxGasLimitDa. */
const txRangeParamsShape = Object.fromEntries(TX_FILTER_DIMENSIONS.flatMap((d) => rangeParamNames(d.key).map((name) => [name, z.string().optional()])));
export const txListQuerySchema = z.object({
    page: z.string().optional(),
    limit: z.string().optional(),
    feePayer: z.string().optional(),
    status: z.string().optional(),
    executionResult: z.string().optional(),
    hasTeardown: z.string().optional(),
    sort: z.string().optional(),
    order: z.string().optional(),
    ...txRangeParamsShape,
});
/**
 * GET /txs/facets: the list filters, plus an optional cluster breakdown —
 * `clusters` (comma-separated ids, -1 for outliers) under `clusterRun`. The
 * run is explicit because cluster ids are renumbered by every analysis run,
 * so the ids only mean something next to the run they were read from.
 */
export const txFacetsQuerySchema = txListQuerySchema.extend({
    clusterRun: z.string().optional(),
    clusters: z.string().optional(),
});
export const feePayerStatSchema = z.object({
    address: z.string(),
    count: z.number(),
    label: z.string().nullable(),
});
export const feePayerStatsResponseSchema = z.object({
    feePayers: z.array(feePayerStatSchema),
});
export const txHashParamSchema = z.object({
    id: z.string(),
    hash: z.string(),
});
// ── TX Comparison ──
export const txCompareRequestSchema = z.object({
    txHashes: z.array(z.string()).min(2).max(5),
});
export const txCompareItemSchema = z.object({
    txHash: z.string(),
    status: z.string(),
    blockNumber: z.number().nullable(),
    feePayer: z.string(),
    actualFee: z.string().nullable(),
    featureVector: z.array(z.union([z.number(), z.string()])).nullable(),
    numNoteHashes: z.number(),
    numNullifiers: z.number(),
    numL2ToL1Msgs: z.number(),
    numPrivateLogs: z.number(),
    numContractClassLogs: z.number(),
    numPublicDataWrites: z.number().nullable(),
    numPublicLogs: z.number().nullable(),
    numSetupCalls: z.number(),
    numAppCalls: z.number(),
    hasTeardown: z.boolean(),
    totalPublicCalldataSize: z.number(),
    gasLimitDa: z.number().nullable(),
    gasLimitL2: z.number().nullable(),
    maxFeePerDaGas: z.number().nullable(),
    maxFeePerL2Gas: z.number().nullable(),
    clusterId: z.number().nullable(),
    clusterSize: z.number().nullable(),
    outlierScore: z.number().nullable(),
});
export const txCompareResponseSchema = z.object({
    transactions: z.array(txCompareItemSchema),
});
//# sourceMappingURL=txs.js.map